import { useOffreService } from '@europrocurement/l2d-domain';
import { CatGorieDesReglesDeCalculJsonldCategoryReglecalculRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';
import React, { createContext, PropsWithChildren, useContext, useEffect, useReducer } from 'react';
import { DataProviderProps, DataState } from './types';

export type RegleCalculCategory = {
    id: number;
    libelle: string;
};

type RegleCalculCategoryState = DataState & {
    data: Array<RegleCalculCategory>;
};

const INIT_ITEMS_CTX = 'init_regle_calcul_categories_ctx';

type Action = { type: 'init_regle_calcul_categories_ctx'; payload: Array<RegleCalculCategory> };

export const RegleCalculCategoriesContext = createContext<RegleCalculCategoryState | null>(null);
export const RegleCalculCategoriesDispatchContext = createContext<React.Dispatch<Action> | null>(
    null,
);

export const regleCalculCategoryApiToStateTransformer = (
    item: CatGorieDesReglesDeCalculJsonldCategoryReglecalculRead,
): RegleCalculCategory => ({
    id: item.id as number,
    libelle: item.libelle as string,
});

function regleCalculCategoriesReducer(
    _state: RegleCalculCategoryState,
    action: Action,
): RegleCalculCategoryState {
    switch (action.type) {
        case INIT_ITEMS_CTX:
            return { loaded: true, data: [...action.payload] };
        default:
            throw new Error(`Unknown action: ${(action as Action).type}`);
    }
}

export const RegleCalculCategoriesProvider: React.FunctionComponent<
    PropsWithChildren<DataProviderProps>
> = function ({ autoload, children }) {
    const [state, dispatch] = useReducer(regleCalculCategoriesReducer, {
        loaded: false,
        data: [],
    });
    const { catgorieDesReglesDeCalculApi } = useOffreService();

    useEffect(() => {
        if (!autoload) {
            return;
        }

        catgorieDesReglesDeCalculApi
            .apiRegleCalculCategoriesGetCollection({ itemsPerPage: 999 })
            .then((response) => {
                dispatch({
                    type: INIT_ITEMS_CTX,
                    payload: response.data['hydra:member'].map(
                        regleCalculCategoryApiToStateTransformer,
                    ),
                });
            })
            .catch((e) => console.error(e));
    }, [catgorieDesReglesDeCalculApi, autoload]);

    return (
        <RegleCalculCategoriesContext.Provider value={state}>
            <RegleCalculCategoriesDispatchContext.Provider value={dispatch}>
                {children}
            </RegleCalculCategoriesDispatchContext.Provider>
        </RegleCalculCategoriesContext.Provider>
    );
};

export function useRegleCalculCategoriesCtx() {
    const state = useContext(RegleCalculCategoriesContext);

    const isDataLoaded = (): boolean => state?.loaded || false;

    const getCategories = (): Array<RegleCalculCategory> => state?.data || [];

    const getCategoryById = (id: number): RegleCalculCategory | undefined =>
        (state?.data || []).find((category) => category.id === id);

    return { isDataLoaded, getCategories, getCategoryById };
}

export function useRegleCalculCategoriesCtxDispatch() {
    return useContext(RegleCalculCategoriesDispatchContext);
}
